import { withMonth, type EarlyRepayment, type Loan, type MonthIndex } from '@finsim/model';
import { Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { KeyDateMonthField, RateField } from '../../../entities/project';
import {
  Button,
  Field,
  IconButton,
  MoneyField,
  DecimalField,
  OptionalMoneyField,
  Section,
  Select,
  Switch,
  EditOnly,
} from '../../../shared/ui';
import { withOptional } from '../lib';
import { CategoryField, WindowFields } from './common-fields';
import type { FormContext } from './context';

export function LoanForm({ item, ctx }: { item: Loan; ctx: FormContext }) {
  const { t } = useTranslation();
  const set = (next: Loan, field: string): void => {
    ctx.commit(next, field);
  };
  const { start: startMonth, horizonMonths } = ctx.project.assumptions;
  const lastPayment = item.start + item.termMonths;
  const repayments = item.earlyRepayments;
  const setRepayments = (earlyRepayments: EarlyRepayment[]): void => {
    set({ ...item, earlyRepayments }, 'earlyRepayments');
  };

  return (
    <>
      <Section title={t('inspector.sections.loan')}>
        <Field label={t('fields.principal')}>
          {({ id }) => (
            <MoneyField
              id={id}
              className="primary-field"
              value={item.principal}
              symbol={ctx.symbol}
              min={1}
              onCommit={(principal) => {
                set({ ...item, principal }, 'principal');
              }}
            />
          )}
        </Field>
        <WindowFields ctx={ctx} item={item} showEnd={false} onChange={set} />
        <Field label={t('fields.termYears')}>
          {({ id }) => (
            <DecimalField
              id={id}
              value={item.termMonths / 12}
              min={1 / 12}
              max={60}
              onCommit={(years) => {
                const termMonths = Math.max(1, Math.round(years * 12));
                const inTerm = repayments.filter((r) => r.month <= item.start + termMonths);
                set({ ...item, termMonths, earlyRepayments: inTerm }, 'termMonths');
              }}
            />
          )}
        </Field>
        <RateField
          label={t('fields.rate')}
          project={ctx.project}
          value={item.rate}
          onCommit={(rate) => {
            set({ ...item, rate }, 'rate');
          }}
        />
        <Field label={t('fields.amortization')}>
          {({ id }) => (
            <Select
              id={id}
              value={item.amortization}
              onValueChange={(amortization) => {
                set({ ...item, amortization }, 'amortization');
              }}
              options={[
                { value: 'annuity', label: t('fields.annuity') },
                { value: 'linear', label: t('fields.linear') },
              ]}
            />
          )}
        </Field>
        <Field label={t('fields.disburse')} hint={t('fields.disburseHint')}>
          {({ id, describedBy }) => (
            <div className="pt-1">
              <Switch
                id={id}
                aria-describedby={describedBy}
                checked={item.disburseToCash}
                onCheckedChange={(disburseToCash) => {
                  set({ ...item, disburseToCash }, 'disburseToCash');
                }}
              />
            </div>
          )}
        </Field>
      </Section>

      <Section
        title={t('inspector.sections.repayments')}
        actions={
          <EditOnly>
            <Button
              size="sm"
              variant="ghost"
              leadingIcon={<Plus />}
              disabled={item.start + 1 > lastPayment}
              onClick={() => {
                const month = Math.min(lastPayment, item.start + 12) as MonthIndex;
                setRepayments([...repayments, { month, amount: 5000, mode: 'reduceTerm' }]);
              }}
            >
              {t('fields.addRepayment')}
            </Button>
          </EditOnly>
        }
      >
        {repayments.map((r, index) => (
          <div
            key={`${String(r.month)}-${String(index)}`}
            className="space-y-1.5 rounded-md border border-border-subtle p-2"
          >
            <div className="flex items-start gap-1.5">
              <KeyDateMonthField
                className="min-w-0 flex-1"
                aria-label={t('fields.repaymentMonth')}
                value={r.month}
                anchor={r.anchors?.month}
                start={startMonth}
                min={item.start + 1}
                max={Math.min(lastPayment, horizonMonths - 1)}
                onCommit={(month, anchor) => {
                  setRepayments(
                    repayments.map((x, i) =>
                      i === index ? withMonth(x, 'month', month, anchor) : x,
                    ),
                  );
                }}
              />
              <EditOnly>
                <IconButton
                  label={t('fields.removeRepayment')}
                  onClick={() => {
                    setRepayments(repayments.filter((_, i) => i !== index));
                  }}
                >
                  <X />
                </IconButton>
              </EditOnly>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <MoneyField
                aria-label={t('fields.repaymentAmount')}
                value={r.amount}
                symbol={ctx.symbol}
                min={1}
                onCommit={(amount) => {
                  setRepayments(repayments.map((x, i) => (i === index ? { ...x, amount } : x)));
                }}
              />
              <Select
                aria-label={t('fields.repaymentMode')}
                value={r.mode}
                options={[
                  { value: 'reduceTerm', label: t('fields.reduceTerm') },
                  { value: 'reducePayment', label: t('fields.reducePayment') },
                ]}
                onValueChange={(mode) => {
                  setRepayments(repayments.map((x, i) => (i === index ? { ...x, mode } : x)));
                }}
              />
            </div>
          </div>
        ))}
      </Section>

      <Section title={t('inspector.sections.fees')}>
        <Field label={t('fields.upfrontFee')}>
          {({ id }) => (
            <OptionalMoneyField
              id={id}
              value={item.upfrontFee}
              symbol={ctx.symbol}
              placeholder={t('fields.noLimit')}
              onCommit={(fee) => {
                set(withOptional(item, 'upfrontFee', fee), 'upfrontFee');
              }}
            />
          )}
        </Field>
        <Field label={t('fields.monthlyFee')}>
          {({ id }) => (
            <OptionalMoneyField
              id={id}
              value={item.monthlyFee}
              symbol={ctx.symbol}
              placeholder={t('fields.noLimit')}
              onCommit={(fee) => {
                set(withOptional(item, 'monthlyFee', fee), 'monthlyFee');
              }}
            />
          )}
        </Field>
        <CategoryField
          ctx={ctx}
          value={item.category}
          kind="expense"
          onChange={(category) => {
            set(withOptional(item, 'category', category as Loan['category']), 'category');
          }}
        />
      </Section>
    </>
  );
}
